import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Upload, Loader2 } from "lucide-react"
import { toastError, toastSuccess } from "@/lib/toast"

export function UploadDialog({ open, onOpenChange }) {
  const [file, setFile] = useState<File | null>(null)
  const [progress, setProgress] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState("")

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (!selected) return

    const allowed = [
      "text/csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ]

    if (!allowed.includes(selected.type)) {
      setError("Only CSV or Excel (.xlsx) files allowed")
      setFile(null)
      return
    }

    setError("")
    setFile(selected)
  }

  const handleUpload = async () => {
    if (!file) return

    setUploading(true)
    setProgress(0)
    setError("")

    const formData = new FormData()
    formData.append("file", file)

    try {
      const xhr = new XMLHttpRequest()

      // 🔥 progress tracking
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100)
          setProgress(percent)
        }
      }

      xhr.onload = async () => {
        setUploading(false)

        if (xhr.status === 200) {
          toastSuccess("Upload successful")

          // 🔥 optional auto reconcile
          try {
            await fetch("/mpesa/autorepay", { method: "POST" })
            toastSuccess("Auto reconciliation completed")
          } catch {
            // don't block UI if this fails
          }

          onOpenChange(false)
          setFile(null)
          setProgress(0)
        } else {
          setError("Upload failed")
          toastError("Upload failed")
        }
      }

      xhr.onerror = () => {
        setUploading(false)
        setError("Network error")
        toastError("Network error during upload")
      }

      // ✅ UPDATED ENDPOINT
      xhr.open("POST", "/mpesa/upload")

      // if you use auth (sanctum/jwt), include cookies
      xhr.withCredentials = true

      xhr.send(formData)
    } catch (err) {
      setUploading(false)
      setError("Something went wrong")
      toastError("Unexpected error")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">
            Upload Mpesa Collections
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* FILE INPUT */}
          <div className="border rounded-md p-3">
            <input
              type="file"
              onChange={handleFileChange}
              className="w-full text-sm"
            />

            {file && (
              <p className="text-sm mt-2 text-muted-foreground">
                Selected: {file.name}
              </p>
            )}

            {error && (
              <p className="text-sm mt-2 text-red-500">{error}</p>
            )}
          </div>

          {/* PROGRESS */}
          {uploading && (
            <div className="space-y-1">
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-right">
                {progress}%
              </p>
            </div>
          )}

          {/* TEMPLATE */}
          <Button
            variant="secondary"
            onClick={() => window.open("/template.xlsx")}
          >
            Download Import Template
          </Button>
        </div>

        <DialogFooter className="mt-6 flex justify-end gap-2">
          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="flex items-center gap-2"
          >
            {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
            <Upload className="h-4 w-4" />
            {uploading ? `Uploading ${progress}%` : "Upload"}
          </Button>

          <Button
            variant="ghost"
            disabled={uploading}
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}